import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { invoicesService, Invoice, InvoiceUpdateData } from "../services/invoices.service";
import { providersService, Provider } from "../services/providers.service";
import { usePdfPreview } from "../hooks/usePdfPreview";
import { openFileInNewTab as openFile } from "../utils/file";
import PdfPreview from "../components/PdfPreview";
import "../styles/EditInvoicePage.css";
import { getErrorMessage } from '../utils/error';

const EditInvoicePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Form state
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [invoiceAmount, setInvoiceAmount] = useState<string>("");
  const [initialAmount, setInitialAmount] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<string>("");
  const [periodStartDate, setPeriodStartDate] = useState<string>("");
  const [consumptionQuantity, setConsumptionQuantity] = useState('');
  const [consumptionUnit, setConsumptionUnit] = useState('kWh');
  // '' = nature inconnue. C'est cet état qui masque l'historique de consommation
  // à l'accueil : le corriger ici est le seul moyen de le retrouver.
  const [consumptionEstimated, setConsumptionEstimated] = useState<'' | 'oui' | 'non'>('');
  const [periodEndDate, setPeriodEndDate] = useState<string>("");

  const pdfPreview = usePdfPreview(invoice?.colocId, invoice?.fileName);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (!id) {
          setError("ID de facture manquant");
          setLoading(false);
          return;
        }

        const invoiceData = await invoicesService.getInvoiceById(id);
        setInvoice(invoiceData);

        setSelectedProviderId(invoiceData.providerId || "");
        setInvoiceAmount(invoiceData.invoiceAmount.toString());
        setInitialAmount(invoiceData.initialAmount.toString());

        if (invoiceData.invoiceDate) {
          const date = new Date(invoiceData.invoiceDate);
          setInvoiceDate(date.toISOString().split("T")[0]);
        }
        if (invoiceData.periodStartDate) {
          const d = new Date(invoiceData.periodStartDate);
          setPeriodStartDate(d.toISOString().split("T")[0]);
        }
        if (invoiceData.periodEndDate) {
          const d = new Date(invoiceData.periodEndDate);
          setPeriodEndDate(d.toISOString().split("T")[0]);
        }
        if (invoiceData.consumptionQuantity != null) {
          setConsumptionQuantity(String(invoiceData.consumptionQuantity));
        }
        if (invoiceData.consumptionUnit) setConsumptionUnit(invoiceData.consumptionUnit);
        if (invoiceData.consumptionEstimated != null) {
          setConsumptionEstimated(invoiceData.consumptionEstimated ? 'oui' : 'non');
        }

        const providersData = await providersService.getProviders();
        const filteredProviders = providersData.filter(
          (provider) => provider.colocId === invoiceData.colocId
        );
        setProviders(filteredProviders);

        setLoading(false);
      } catch (err) {
        setError(getErrorMessage(err, "Échec du chargement des données"));
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) {
      setError("ID de facture manquant");
      return;
    }

    if ((periodStartDate && !periodEndDate) || (!periodStartDate && periodEndDate)) {
      setError("Les dates de début et fin de période doivent être saisies ensemble");
      return;
    }

    // Même règle que l'écran des journaux (ticket #47) : nul refusé, négatif =
    // avoir, confirmé pour qu'une erreur de signe ne passe pas en silence.
    const montants = [initialAmount, invoiceAmount].filter(m => m !== "").map(parseFloat);
    if (montants.some(m => isNaN(m) || m === 0)) {
      setError("Les montants doivent être des nombres non nuls");
      return;
    }
    if (montants.some(m => m < 0) && !window.confirm("Ce montant négatif correspond à un avoir ?")) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const updateData: InvoiceUpdateData = {};

      if (selectedProviderId && selectedProviderId !== invoice?.providerId) {
        updateData.providerId = selectedProviderId;
      }
      if (invoiceAmount && parseFloat(invoiceAmount) !== invoice?.invoiceAmount) {
        updateData.invoiceAmount = parseFloat(invoiceAmount);
      }
      if (initialAmount && parseFloat(initialAmount) !== invoice?.initialAmount) {
        updateData.initialAmount = parseFloat(initialAmount);
      }
      if (invoiceDate) {
        const currentDate = invoice?.invoiceDate ? new Date(invoice.invoiceDate).toISOString().split("T")[0] : "";
        if (invoiceDate !== currentDate) {
          updateData.invoiceDate = new Date(invoiceDate).toISOString();
        }
      }
      {
        const currentStart = invoice?.periodStartDate ? new Date(invoice.periodStartDate).toISOString().split("T")[0] : "";
        if (periodStartDate !== currentStart) {
          updateData.periodStartDate = periodStartDate ? new Date(periodStartDate).toISOString() : null;
        }
      }
      {
        const currentEnd = invoice?.periodEndDate ? new Date(invoice.periodEndDate).toISOString().split("T")[0] : "";
        if (periodEndDate !== currentEnd) {
          updateData.periodEndDate = periodEndDate ? new Date(periodEndDate).toISOString() : null;
        }
      }

      {
        const actuelle = invoice?.consumptionQuantity != null
          ? String(invoice.consumptionQuantity) : "";
        if (consumptionQuantity !== actuelle) {
          // Vider le champ efface la consommation : c'est une correction
          // légitime quand le LLM a lu de travers.
          updateData.consumptionQuantity =
            consumptionQuantity === "" ? null : parseFloat(consumptionQuantity);
          updateData.consumptionUnit =
            consumptionQuantity === "" ? null : consumptionUnit;
        } else if (consumptionQuantity !== "" && consumptionUnit !== (invoice?.consumptionUnit || "")) {
          updateData.consumptionUnit = consumptionUnit;
        }
      }

      {
        const actuelle = invoice?.consumptionEstimated == null
          ? '' : invoice.consumptionEstimated ? 'oui' : 'non';
        if (consumptionEstimated !== actuelle) {
          updateData.consumptionEstimated =
            consumptionEstimated === '' ? null : consumptionEstimated === 'oui';
        }
      }

      if (Object.keys(updateData).length === 0) {
        setSuccess("Aucune modification effectuée");
        setLoading(false);
        return;
      }

      await invoicesService.updateInvoice(id!, updateData);
      setSuccess("Facture mise à jour avec succès");
      setTimeout(() => navigate("/invoices"), 2000);
    } catch (err) {
      setError(getErrorMessage(err, "Échec de la mise à jour de la facture"));
      setLoading(false);
    }
  };

  const handleOpenFileInNewTab = () => {
    if (!invoice?.fileName || !invoice?.colocId) return;
    openFile(invoice.colocId, invoice.fileName);
  };

  const handleDeleteInvoice = async () => {
    if (!id) {
      setError("ID de facture manquant");
      return;
    }

    const confirmed = window.confirm("Êtes-vous sûr de vouloir supprimer cette facture ? Cette action est irréversible.");
    if (!confirmed) return;

    setIsDeleting(true);
    setError(null);

    try {
      await invoicesService.deleteInvoice(id!);
      setSuccess("Facture supprimée avec succès");
      setTimeout(() => navigate("/invoices"), 1500);
    } catch (err) {
      setError(getErrorMessage(err, "Échec de la suppression de la facture"));
      setIsDeleting(false);
    }
  };

  const handleValidateInvoice = async () => {
    if (!id) {
      setError("ID de facture manquant");
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const updateData: InvoiceUpdateData = { isValidated: true };
      await invoicesService.updateInvoice(id, updateData);
      setSuccess("Facture validée avec succès");
      setTimeout(() => navigate("/invoices"), 1000);
    } catch (err) {
      setError(getErrorMessage(err, "Échec de la validation de la facture"));
      setIsValidating(false);
    }
  };
  
  if (loading && !invoice) {
    return <div className="loading-indicator">Chargement des données de la facture...</div>;
  }
  
  if (error && !invoice) {
    return <div className="error-message">{error}</div>;
  }
  
  if (!invoice) {
    return <div className="error-message">Facture introuvable</div>;
  }
  
  return (
    <div className="edit-invoice-container">
      <div className="edit-invoice-header">
        <h2>Modifier la facture</h2>
        <div className="header-buttons">
          <button 
            className={`validate-invoice-button ${isValidating ? 'validating' : ''} ${invoice.isValidated ? 'disabled' : ''}`}
            onClick={handleValidateInvoice}
            disabled={isValidating || loading || invoice.isValidated}
            title={invoice.isValidated ? 'Cette facture est déjà validée' : 'Valider cette facture'}
          >
            {isValidating ? 'Validation...' : invoice.isValidated ? 'Facture validée' : 'Valider la facture'}
          </button>
          <button 
            className={`delete-invoice-button ${isDeleting ? 'deleting' : ''}`}
            onClick={handleDeleteInvoice}
            disabled={isDeleting || loading}
          >
            {isDeleting ? 'Suppression...' : 'Supprimer la facture'}
          </button>
        </div>
      </div>
      
      <div className="edit-invoice-content">
        <div className="edit-invoice-form-section">
          <div className="invoice-details">
            <div className="detail-row">
              <span className="detail-label">Nom du fichier:</span>
              <span className="detail-value">{invoice.fileName || 'Non disponible'}</span>
            </div>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <form onSubmit={handleSubmit} className="edit-invoice-form">
            <div className="form-group">
              <label htmlFor="provider">Fournisseur:</label>
              <select
                id="provider"
                value={selectedProviderId}
                onChange={(e) => setSelectedProviderId(e.target.value)}
                className="form-control"
                required
              >
                <option value="">Sélectionner un fournisseur</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.providerName}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="initialAmount">Montant initial:</label>
              <input
                type="number"
                id="initialAmount"
                value={initialAmount}
                onChange={(e) => setInitialAmount(e.target.value)}
                step="0.01"
                className="form-control"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="amount">Montant final:</label>
              <input
                type="number"
                id="amount"
                value={invoiceAmount}
                onChange={(e) => setInvoiceAmount(e.target.value)}
                step="0.01"
                className="form-control"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="invoiceDate">Date de la facture:</label>
              <input
                type="date"
                id="invoiceDate"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label>Période de consommation (optionnel):</label>
              <div className="period-fields">
                <input
                  type="date"
                  id="periodStartDate"
                  value={periodStartDate}
                  onChange={(e) => setPeriodStartDate(e.target.value)}
                  className="form-control"
                />
                <span className="period-separator">→</span>
                <input
                  type="date"
                  id="periodEndDate"
                  value={periodEndDate}
                  onChange={(e) => setPeriodEndDate(e.target.value)}
                  className="form-control"
                />
                {(periodStartDate || periodEndDate) && (
                  <button type="button" className="clear-date-button" onClick={() => { setPeriodStartDate(""); setPeriodEndDate(""); }} title="Effacer la période">×</button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="consumptionQuantity">Consommation (optionnel):</label>
              <div className="consumption-fields">
                <input
                  type="number"
                  id="consumptionQuantity"
                  value={consumptionQuantity}
                  onChange={(e) => setConsumptionQuantity(e.target.value)}
                  className="form-control"
                  step="0.001"
                  min="0"
                  placeholder="Quantité"
                />
                <select
                  id="consumptionUnit"
                  value={consumptionUnit}
                  onChange={(e) => setConsumptionUnit(e.target.value)}
                  className="form-control"
                  aria-label="Unité de consommation"
                >
                  <option value="kWh">kWh</option>
                  <option value="m3">m³</option>
                  <option value="MWh">MWh</option>
                </select>
              </div>
              <small className="field-hint">
                Corrigible même sur une facture déjà répartie : la consommation
                n'entre dans aucun calcul de charges.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="consumptionEstimated">Nature du relevé:</label>
              <select
                id="consumptionEstimated"
                value={consumptionEstimated}
                onChange={(e) => setConsumptionEstimated(e.target.value as '' | 'oui' | 'non')}
                className="form-control"
              >
                <option value="">Non renseignée</option>
                <option value="non">Relevé réel (compteur communicant, index relevé)</option>
                <option value="oui">Estimation (compteur non accessible)</option>
              </select>
              <small className="field-hint">
                L'historique de consommation n'est affiché que si toutes les
                factures de l'énergie portent un relevé réel. Une nature non
                renseignée le masque.
              </small>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/invoices')}
                className="cancel-button"
                disabled={loading}
              >
                Annuler
              </button>
              
              <button
                type="submit"
                className="save-button"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        </div>
        
        <div className="file-preview-section">
          <h2>Aperçu du fichier</h2>
          <PdfPreview
            isPdf={pdfPreview.isPdf}
            isImage={pdfPreview.isImage}
            dataUrl={pdfPreview.dataUrl}
            directFileUrl={pdfPreview.directFileUrl}
            fileError={pdfPreview.fileError}
            fileName={invoice.fileName}
            onOpenInNewTab={handleOpenFileInNewTab}
          />
        </div>
      </div>
    </div>
  );
};

export default EditInvoicePage;
